/**
 * src/pages/Scraping.jsx
 * Panel de control de scraping: trigger manual, estado y logs.
 */
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Header } from '../components/layout/Header'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge, RESULTADO_COLOR } from '../components/ui/Badge'
import { Table } from '../components/ui/Table'
import { useEstado, useLogs, useTrigger } from '../hooks/useScraping'
import { useCliente } from '../hooks/useClientes'
import { getDiagnosticoCatalogo } from '../api/scraping'

function EstadoSection({ estado }) {
  if (!estado) return null

  function Block({ label, data }) {
    return (
      <div style={{
        padding: '14px', background: 'var(--color-surface-2)',
        border: '1px solid var(--color-border)', borderRadius: '8px',
        flex: 1,
      }}>
        <div style={{
          fontSize: '10px', fontWeight: 500, color: 'var(--color-text-3)',
          fontFamily: 'var(--font-mono)', letterSpacing: '0.1em', marginBottom: '10px',
        }}>
          {label}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {[
            ['Estado', <Badge color={data.activo ? 'green' : 'gray'} dot pulse={data.activo}>{data.activo ? 'Activo' : 'Inactivo'}</Badge>],
            ['Frecuencia', <span style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: 'var(--color-text)' }}>{data.frecuencia}</span>],
            ['Último run', data.ultimo_run
              ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--color-text-2)' }}>{new Date(data.ultimo_run).toLocaleString('es-CL')}</span>
              : <span style={{ color: 'var(--color-text-3)', fontSize: '12px' }}>—</span>],
            ['Próximo run', data.proximo_run
              ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--color-text-2)' }}>{new Date(data.proximo_run).toLocaleString('es-CL')}</span>
              : <span style={{ color: 'var(--color-text-3)', fontSize: '12px' }}>—</span>],
          ].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: 'var(--color-text-3)' }}>{k}</span>
              {v}
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
      <Block label="PRECIOS" data={estado.precios} />
      <Block label="ÓRDENES DE COMPRA" data={estado.ocs} />
    </div>
  )
}

function TriggerPanel({ clienteId, onSuccess }) {
  const trigger = useTrigger(clienteId)
  const [tipo, setTipo] = useState('ambos')
  const [result, setResult] = useState(null)

  const TIPOS = [
    { value: 'precios', label: 'Precios', color: '#3b82f6' },
    { value: 'ocs', label: 'OCs', color: '#f59e0b' },
    { value: 'ocs_full', label: 'OCs histórico', color: '#b45309' },
    { value: 'ambos', label: 'Ambos', color: 'var(--color-accent)' },
    { value: 'dashboard', label: 'Solo dashboard', color: '#0f766e' },
  ]
  const tipoLabel = TIPOS.find(t => t.value === tipo)?.label || tipo

  async function handleTrigger() {
    setResult(null)
    try {
      const data = await trigger.mutateAsync({ tipo })
      setResult({ ok: data.ok, duracion: data.duracion_s, mensaje: data.mensaje })
      onSuccess?.()
    } catch (e) {
      setResult({ ok: false, mensaje: e.response?.data?.detail || 'Error al ejecutar' })
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {/* Tipo selector */}
      <div>
        <div style={{
          fontSize: '11px', fontWeight: 500, color: 'var(--color-text-3)',
          fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', marginBottom: '8px',
        }}>
          TIPO DE SCRAPING
        </div>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {TIPOS.map(t => (
            <button
              key={t.value}
              onClick={() => setTipo(t.value)}
              style={{
                padding: '7px 16px', borderRadius: '6px', fontSize: '13px',
                fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s',
                border: `1px solid ${tipo === t.value ? t.color : 'var(--color-border)'}`,
                background: tipo === t.value ? `${t.color}18` : 'transparent',
                color: tipo === t.value ? t.color : 'var(--color-text-3)',
                fontFamily: 'var(--font-ui)',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
        {tipo === 'ocs_full' && (
          <div style={{
            marginTop: '8px', fontSize: '11px', color: 'var(--color-text-3)',
            fontFamily: 'var(--font-ui)', lineHeight: 1.4,
          }}>
            Reconstruye el histórico completo (365 días) de todas las empresas y
            competidores. Más lento que el OCs normal; úsalo para backfill o para
            poblar la Competencia de competidores recién añadidos.
          </div>
        )}
        {tipo === 'dashboard' && (
          <div style={{
            marginTop: '8px', fontSize: '11px', color: 'var(--color-text-3)',
            fontFamily: 'var(--font-ui)', lineHeight: 1.4,
          }}>
            Regenera el dashboard con los datos que ya tiene el cliente, sin
            scraping (segundos). Úsalo tras un cambio de plan o de plantilla para
            que el cliente vea los cambios sin esperar al run de la noche.
          </div>
        )}
      </div>

      {/* Botón trigger */}
      <Button
        onClick={handleTrigger}
        loading={trigger.isPending}
        icon={
          !trigger.isPending && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
          )
        }
      >
        {trigger.isPending ? 'Ejecutando pipeline...' : `Ejecutar · ${tipoLabel}`}
      </Button>

      {/* Resultado */}
      {result && (
        <div style={{
          padding: '10px 12px', borderRadius: '7px',
          background: result.ok ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
          border: `1px solid ${result.ok ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)'}`,
          fontSize: '12px', fontFamily: 'var(--font-mono)',
          color: result.ok ? 'var(--color-ok)' : 'var(--color-error)',
        }}>
          {result.ok ? '✓' : '✗'} {result.mensaje}
          {result.duracion != null && <span style={{ color: 'var(--color-text-3)', marginLeft: '8px' }}>{result.duracion}s</span>}
        </div>
      )}
    </div>
  )
}

const LOG_COLS = [
  {
    key: 'tipo', label: 'Tipo', mono: true, nowrap: true,
    render: v => <span style={{ fontFamily: 'var(--font-mono)', fontSize: '12px' }}>{v}</span>,
  },
  {
    key: 'inicio', label: 'Inicio', nowrap: true,
    render: v => v ? new Date(v).toLocaleString('es-CL') : '—',
  },
  {
    key: 'fin', label: 'Fin', nowrap: true,
    render: v => v ? new Date(v).toLocaleString('es-CL') : '—',
  },
  {
    key: 'resultado', label: 'Resultado', width: '100px',
    render: v => v
      ? <Badge color={RESULTADO_COLOR[v] || 'gray'} dot pulse={v === 'running'}>{v}</Badge>
      : <Badge color="gray">—</Badge>,
  },
]

function DiagnosticoCatalogo({ clienteId }) {
  const [d, setD] = useState(null)
  const [loading, setLoading] = useState(false)

  async function run() {
    setLoading(true); setD(null)
    try { setD(await getDiagnosticoCatalogo(clienteId)) }
    catch (e) { setD({ error: e.response?.data?.detail || 'No se pudo leer el diagnóstico' }) }
    finally { setLoading(false) }
  }

  const Group = ({ titulo, filas }) => (
    <div style={{ minWidth: '220px', flex: '1 1 220px' }}>
      <div style={{ fontSize: '11px', letterSpacing: '0.08em', color: 'var(--color-text-3)',
                    fontFamily: 'var(--font-mono)', marginBottom: '6px' }}>{titulo}</div>
      {filas.map(([k, v]) => (
        <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: '16px',
                              padding: '6px 0', borderBottom: '1px solid var(--color-border)', fontSize: '13px' }}>
          <span style={{ color: 'var(--color-text-2)' }}>{k}</span>
          <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-text)', whiteSpace: 'nowrap' }}>{v}</span>
        </div>
      ))}
    </div>
  )

  return (
    <div>
      <Button size="sm" onClick={run} loading={loading}>Ver embudo OCs → catálogo</Button>
      {d?.error && (
        <div style={{ marginTop: '12px', fontSize: '12px', color: 'var(--color-error)',
                      fontFamily: 'var(--font-mono)' }}>✗ {d.error}</div>
      )}
      {d && !d.error && (
        <>
          <div style={{ marginTop: '14px', display: 'flex', flexWrap: 'wrap', gap: '28px' }}>
            <Group titulo="OCS_COMPLETO" filas={[
              ['OCs totales', d.ocs_completo.total],
              ['OCs propias', d.ocs_completo.propias],
            ]} />
            <Group titulo="OCS_DETALLE" filas={[
              ['OCs con detalle', `${d.ocs_detalle.ocs_con_productos}/${d.ocs_detalle.ocs}`],
              ['Líneas', d.ocs_detalle.lineas],
              ['Códigos CM distintos', d.ocs_detalle.codigos_cm_distintos],
            ]} />
            <Group titulo="CATÁLOGO" filas={[
              ['Códigos de OCs', d.catalogo.total_codigos_oc ?? '—'],
              ['En catálogo', d.catalogo.en_catalogo],
              ['Descartados', d.catalogo.descartados],
            ]} />
          </div>
          <div style={{ fontSize: '11px', color: 'var(--color-text-3)', fontFamily: 'var(--font-ui)',
                        lineHeight: 1.5, marginTop: '14px' }}>
            {d.lectura}
          </div>
          {/* JSON crudo: garantiza legibilidad completa aunque el resumen quede estrecho */}
          <details style={{ marginTop: '12px' }}>
            <summary style={{ cursor: 'pointer', fontSize: '12px', color: 'var(--color-text-2)',
                              fontFamily: 'var(--font-mono)' }}>Ver JSON crudo</summary>
            <pre style={{ marginTop: '10px', padding: '12px', background: 'var(--color-surface-2)',
                          border: '1px solid var(--color-border)', borderRadius: '8px', fontSize: '11px',
                          fontFamily: 'var(--font-mono)', color: 'var(--color-text)', whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word', overflowX: 'auto', maxHeight: '420px' }}>
              {JSON.stringify(d, null, 2)}
            </pre>
          </details>
        </>
      )}
    </div>
  )
}

export default function Scraping() {
  const { clienteId } = useParams()
  const navigate = useNavigate()

  const { data: cliente } = useCliente(clienteId)
  const { data: estado, isLoading: loadingEstado } = useEstado(clienteId)
  const { data: logs = [], isLoading: loadingLogs } = useLogs(clienteId, 30)

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Header
        title="Scraping"
        subtitle={cliente ? `${cliente.nombre} · ${clienteId}` : clienteId}
        action={
          <Button size="sm" variant="ghost" onClick={() => navigate(`/clientes/${clienteId}`)}>
            ← Cliente
          </Button>
        }
      />

      <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '16px', alignItems: 'start', flexShrink: 0 }}>
          {/* Estado */}
          <Card title="Estado del scheduler" subtitle="Configuración actual">
            {loadingEstado ? (
              <div style={{ color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)', fontSize: '12px' }}>Cargando...</div>
            ) : (
              <EstadoSection estado={estado} />
            )}
          </Card>

          {/* Trigger */}
          <Card title="Ejecución manual">
            <TriggerPanel clienteId={clienteId} />
          </Card>
        </div>

        {/* Diagnóstico del catálogo */}
        <Card
          title="Diagnóstico del catálogo"
          subtitle="Por qué el catálogo tiene los códigos que tiene (OCs → filtro tienda)"
          style={{ flexShrink: 0 }}
        >
          <DiagnosticoCatalogo clienteId={clienteId} />
        </Card>

        {/* Logs */}
        <Card
          title="Historial de runs"
          subtitle={`${logs.length} ejecuciones recientes`}
          style={{ flexShrink: 0 }}
        >
          <Table
            columns={LOG_COLS}
            data={logs}
            loading={loadingLogs}
            emptyMessage="Sin ejecuciones registradas"
          />
        </Card>
      </div>
    </div>
  )
}
